import * as React from 'react';
import { useMutation, useQueries, useQuery } from 'react-query';
import { getPatientStats } from 'pages/Dashboard/services/freeTrial.service';
import { Box, TablePagination, Typography, debounce, useMediaQuery } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import useSearchParamsState from 'hooks/use-search-params-state';
import ListPatientFilters from 'pages/Dashboard/components/ListPatientFilters';
import ListPatients from 'pages/Dashboard/components/ListPatientNew';
import { RefetchPatientsCtx } from 'pages/Dashboard/constants';
import { PATIENT_LIST_VIEW } from 'pages/Dashboard/enums';
import usePatientList from 'pages/Dashboard/hooks/usePatientList';
import {
  fetchProvidersAssociationsList,
  fetchUserMetadataWithKeys,
  postUserMetadata,
} from 'pages/Dashboard/services/provider.services';
import { providers } from 'utils/apiUrls';
import { UserMetadata, UserMetadataFlags } from 'pages/Dashboard/types';
import { PracticeProvider } from 'pages/Dashboard/types/providers.types';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import { logSentryInfo, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import Theme from 'theme';
import { getSessionStorageNumber, getSessionStorageValue, setSessionStorageValue } from 'utils/storageUtils';
import { StepModal } from 'pages/OnboardingFlow/OnboardingStepModal';
import EditGoalDrawer from 'pages/Dashboard/EditGoalDrawer';
import DashboardStatsCard from 'pages/Dashboard/components/DashboardStatsCard';

const batchSize = 50;

export default function DashboardV2() {
  const {
    http,
    providerId,
    addPatientGoal: addPatientGoalFromContext,
    setAddPatientGoal,
  } = useHttp();
  const { user } = useUnifiedAuth();

  const isMobile = useMediaQuery('(max-width:600px)');

  const [statusFilter, setStatusFilter] = useSearchParamsState('status', PATIENT_LIST_VIEW.READY);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortField, setSortField] = React.useState<string | null>('');
  const [sortDirection, setSortDirection] = React.useState<string | null>('');
  const defaultSortField = getSessionStorageValue(UserMetadataFlags.DefaultSortField);
  const defaultSortDirection = getSessionStorageValue(UserMetadataFlags.DefaultSortDirection);
  const defaultNumberOfRows = getSessionStorageNumber(UserMetadataFlags.NumberOfRowsPerPage) ?? 0;
  const [lastReadySortField, setlastReadySortField] = React.useState(defaultSortField);
  const [lastReadySortDirection, setlastReadySortDirection] = React.useState(defaultSortDirection);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(
    defaultNumberOfRows ?? batchSize,
  );
  const [expandedPatientId, setExpandedPatientId] = React.useState<string | null>(null);
  const [providerIdFilter, setProviderIdFilter] = React.useState<string>('');
  const handleProviderIdFilterChange = (provider: string) => {
    setProviderIdFilter(provider);
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_PROVIDER_DROPDOWN_FILTER_SELECTED, {
      selectedProviderId: provider,
    });
  };

  // StepModal state
  const [showStepModal, setShowStepModal] = React.useState(false);
  const [stepModalStep, setStepModalStep] = React.useState(0);

  const [showEditGoalDrawer, setShowEditGoalDrawer] = React.useState(false);

  const [isOnboarded, setIsOnboarded] = React.useState('true');

  const handleSortFieldChange = (value: string) => {
    if (statusFilter === PATIENT_LIST_VIEW.READY) {
      setAddPatientPreference(UserMetadataFlags.DefaultSortField, value);
      setlastReadySortField(value);
      setSessionStorageValue(UserMetadataFlags.DefaultSortField, value);
    }
    setSortField(value);
    setPage(0);
  };

  const handleSortDirectionChange = (value: string) => {
    if (statusFilter === PATIENT_LIST_VIEW.READY) {
      setAddPatientPreference(UserMetadataFlags.DefaultSortDirection, value);
      setSessionStorageValue(UserMetadataFlags.DefaultSortDirection, value);
      setlastReadySortDirection(value);
    }
    setSortDirection(value);
    setPage(0);
  };

  const filters = React.useMemo(
    () => ({
      status: statusFilter,
    }),
    [statusFilter],
  );

  const processFlagData = async (data: UserMetadata) => {
    const finalData: UserMetadata = await data;
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_PROVIDER_METADATA_LOADED, {
      flag: finalData.flag.name,
      value: finalData.flag.value ?? '',
    });
    setSessionStorageValue(finalData.flag.name, finalData.flag.value ?? '');
    if (finalData.flag.name === UserMetadataFlags.NumberOfRowsPerPage) {
      setRowsPerPage(parseInt(finalData.flag.value ?? '', 10) ?? 0);
    }
    if (finalData.flag.name === UserMetadataFlags.DefaultSortDirection) {
      if (statusFilter === PATIENT_LIST_VIEW.READY) {
        setSortDirection(finalData.flag.value ?? '');
      }
      setlastReadySortDirection(finalData.flag.value ?? '');
    }
    if (finalData.flag.name === UserMetadataFlags.DefaultSortField) {
      if (statusFilter === PATIENT_LIST_VIEW.READY) {
        setSortField(finalData.flag.value ?? '');
      }
      setlastReadySortField(finalData.flag.value ?? '');
    }
  };

  useQueries([
    {
      queryKey: [providerId, UserMetadataFlags.NumberOfRowsPerPage],
      queryFn: fetchUserMetadataWithKeys(http.post),
      onSuccess: processFlagData,
      onError: () => {
        setSessionStorageValue(UserMetadataFlags.NumberOfRowsPerPage, batchSize.toString());
        setRowsPerPage(batchSize);
      },
    },
    {
      queryKey: [providerId, UserMetadataFlags.DefaultSortDirection],
      queryFn: fetchUserMetadataWithKeys(http.post),
      onSuccess: processFlagData,
      onError: () => {
        // Handle error
        setSessionStorageValue(UserMetadataFlags.DefaultSortDirection, 'desc');
        setSortDirection('desc');
      },
    },
    {
      queryKey: [providerId, UserMetadataFlags.DefaultSortField],
      queryFn: fetchUserMetadataWithKeys(http.post),
      onSuccess: processFlagData,
      onError: () => {
        // Handle error
        setSessionStorageValue(UserMetadataFlags.DefaultSortField, 'dateAdded');
        setSortField('dateAdded');
      },
    },
    {
      queryKey: [providerId, UserMetadataFlags.IsOnboardedFlag],
      queryFn: fetchUserMetadataWithKeys(http.post),
      onSuccess: (data: UserMetadata) => {
        setIsOnboarded(data.flag.value ?? '');
      },
      onError: () => {
        setIsOnboarded('true');
      },
      retry: 0,
    },
  ]);
  const isLoading = (defaultSortField === null
    || defaultSortDirection === null
    || defaultNumberOfRows === null);
  const {
    isFetching,
    isError,
    filteredPatientList,
    patientsCount,
    demoPatientsCount,
    isLoadingPatientsList,
    refetchPatientsList,
    refetchPatientsCount,
  } = usePatientList(
    providerId,
    filters,
    null,
    searchQuery,
    sortDirection,
    sortField,
    page * rowsPerPage,
    rowsPerPage,
    isLoading,
    providerIdFilter as string,
    true,
  );

  const setUserMetadataMutation = useMutation({
    mutationFn: (payload: UserMetadata) => postUserMetadata(http.post, { ...payload }),
  });

  const setAddPatientPreference = React.useCallback(
    (name: UserMetadataFlags, val: string) => {
      setUserMetadataMutation.mutate({
        userId: providerId,
        flag: {
          name,
          value: val,
        },
      });
    },
    [setUserMetadataMutation, providerId],
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_PATIENT_TABLE_PAGINATION_PAGE_CHANGED, {
      currentPage: page?.toString() ?? '',
      nextPage: newPage?.toString() ?? '',
    });
    setPage(newPage);
  };

  const handleChangeExpandedPatientId = (patientId: string) => {
    if (patientId === expandedPatientId) {
      setExpandedPatientId(null);
    }
    else {
      setExpandedPatientId(patientId);
    }
  };

  const providerAssociations = useQuery(
    [`${providers.providerAssociationList.queryUrl}`, providerId, 'dashboard'],
    {
      queryFn: fetchProvidersAssociationsList(http.get),
    },
  );

  // Always reset step to 0 when opening StepModal via info icon
  const openStepModal = React.useCallback(() => {
    setStepModalStep(0);
    setShowStepModal(true);
  }, []);

  const updateStatusFilter = React.useCallback(
    (updatedStatusFilter: string) => {
      setStatusFilter(updatedStatusFilter);
      trackMixpanelEvent(MixpanelEventName
        .DASHBOARD_STATUS_TAB_FILTER_SELECTED, { status: updatedStatusFilter });
    },
    [setStatusFilter],
  );
  React.useEffect(() => {
    logSentryInfo('Dashboard_loaded');
  }, []);

  React.useEffect(() => {
    if (statusFilter !== PATIENT_LIST_VIEW.READY) {
      setSortField('dateAdded');
      setSortDirection('desc');
    }
    else {
      setSortField(lastReadySortField);
      setSortDirection(lastReadySortDirection);
    }
  }, [statusFilter, lastReadySortField, lastReadySortDirection]);

  React.useEffect(() => {
    if (statusFilter !== PATIENT_LIST_VIEW.READY) {
      setSortField('dateAdded');
      setSortDirection('desc');
    }
    else {
      setSortField(lastReadySortField);
      setSortDirection(lastReadySortDirection);
    }
  }, [statusFilter, lastReadySortField, lastReadySortDirection]);

  const refetchPatients = React.useCallback(() => {
    refetchPatientsList();
    refetchPatientsCount();
  }, [refetchPatientsList, refetchPatientsCount]);

  const refetchPatientsCtx = React.useMemo(
    () => ({
      refetchPatients,
    }),
    [refetchPatients],
  );

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setAddPatientPreference(UserMetadataFlags.NumberOfRowsPerPage, event.target.value.toString());
    setSessionStorageValue(UserMetadataFlags.NumberOfRowsPerPage, event.target.value.toString());
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_PATIENT_TABLE_PAGINATION_ROWS_PER_PAGE_CHANGED, {
      numberOfRows: event?.target?.value?.toString() ?? '',
    });
    setPage(0);
  };

  const currentPatientCount = patientsCount?.[statusFilter as PATIENT_LIST_VIEW] ?? (0 as number);
  const handleSearch = (value: string) => {
    setPage(0);
    setSearchQuery(value);
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_SEARCH_BY_NAME_SEARCHED, {});
  };

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter, providerIdFilter]);

  React.useEffect(() => {
    if (sortField && sortDirection) {
      trackMixpanelEvent(MixpanelEventName
        .DASHBOARD_SORTABLE_TABLE_HEADER_APPLIED, { sortField, sortDirection });
    }
  }, [sortField, sortDirection]);

  React.useEffect(() => {
    setExpandedPatientId(null);
  }, [statusFilter, page, searchQuery, sortDirection, sortField]);

  const debouncedSearchQuery = debounce(handleSearch, 500);
  const isSomethingExpanded = expandedPatientId !== null;
  const providerIdFilterList = React.useMemo(() => {
    if (
      providerAssociations.data?.providers?.length
      && providerAssociations.data.providers.length > 0
    ) {
      const allOption: PracticeProvider = {
        id: 'all',
        firstName: 'All',
        lastName: '',
        npiNumber: '',
        designation: '',
        email: '',
      };

      const youOption: PracticeProvider = {
        id: providerId,
        firstName: `${user?.name} (You)`,
        lastName: '',
        npiNumber: '',
        designation: '',
        email: '',
      };

      const sortedProviders = [...providerAssociations.data.providers].sort((a, b) => {
        const lastNameA = a.lastName?.toLowerCase() ?? '';
        const lastNameB = b.lastName?.toLowerCase() ?? '';
        return lastNameA.localeCompare(lastNameB);
      });

      return [allOption, youOption, ...sortedProviders];
    }
    else {
      return [];
    }
  }, [providerAssociations, providerId, user?.name]);

  // Show onboarding modal by default only once after silent login
  React.useEffect(() => {
    if (isOnboarded === 'false') {
      setStepModalStep(0);
      setShowStepModal(true);
    }
  }, [isOnboarded, showStepModal]);

  // Fetch patient stats for provider (must be before any early returns)
  const {
    data: patientStats,
    isLoading: isPatientStatsLoading,
  } = useQuery(
    ['patientStats', providerId],
    () => getPatientStats(http.get, providerId),
    { enabled: !!providerId },
  );

  const activatedPatients = (patientsCount?.[`${PATIENT_LIST_VIEW.READY}-initial`] ?? 0)
    - (demoPatientsCount.READY ?? 0) as number;
  const pendingPatients = patientsCount?.[`${PATIENT_LIST_VIEW.PENDING}-initial`] ?? 0;


  const numWithHealthRecords = (patientStats?.noOfPatientsWithHealthRecords ?? 0)
   - (demoPatientsCount.READY ?? 0);
  const percentageWithHealthRecords = activatedPatients
    > 0 ? Math.round((numWithHealthRecords / activatedPatients) * 100) : 0;
  const numWithpercentageEngaged = (patientStats?.noOfPatientsEngaged ?? 0)
   - (demoPatientsCount.READY ?? 0);
  const percentageWithPercentageEngaged = activatedPatients
    > 0 ? Math.round((numWithpercentageEngaged / activatedPatients) * 100) : 0;
  const addPatientGoal = Number(addPatientGoalFromContext) || 0;
  const showProgressBar = (providerIdFilterList.length <= 0
     || providerId === providerIdFilter)
     && addPatientGoalFromContext && Number(addPatientGoalFromContext) !== 0
     && isOnboarded === 'true' && !providerAssociations.isLoading
     && !isMobile;
  const isLoadingProgressBarData = isPatientStatsLoading || (
    isLoadingPatientsList && filters.status === PATIENT_LIST_VIEW.READY
    && patientsCount?.[PATIENT_LIST_VIEW.READY] === undefined
  );

  return (
    <RefetchPatientsCtx.Provider value={refetchPatientsCtx}>
      <StepModal
        open={showStepModal}
        step={stepModalStep}
        onNext={() => {
          if (stepModalStep < 3) {
            setStepModalStep(stepModalStep + 1);
          }
          else {
            setShowStepModal(false);
          }
        }}
        onClose={() => {
          setShowStepModal(false);
          if (isOnboarded === 'false') {
            setIsOnboarded('true');
            setAddPatientPreference(UserMetadataFlags.IsOnboardedFlag, 'true');
          }
        }}
        totalSteps={4}
      />
      {showProgressBar && (
      <>
        <EditGoalDrawer
          isOpen={showEditGoalDrawer}
          closeDrawer={() => setShowEditGoalDrawer(false)}
          initialGoal={addPatientGoal}
          onSave={(newGoal) => {
            setAddPatientGoal(newGoal.toString());
            setShowEditGoalDrawer(false);
          }}
          leastGoal={Math.max(activatedPatients + pendingPatients, 1)}
        />
        <DashboardStatsCard
          isPatientStatsLoading={isLoadingProgressBarData}
          openStepModal={openStepModal}
          setShowEditGoalDrawer={setShowEditGoalDrawer}
          addPatientGoal={addPatientGoal}
          activatedPatients={activatedPatients}
          pendingPatients={pendingPatients}
          percentageWithHealthRecords={percentageWithHealthRecords}
          percentageWithPercentageEngaged={percentageWithPercentageEngaged}
        />
      </>
      )}
      <Box
        sx={{
          maxWidth: '1350px',
          mx: 'auto',
          width: '100%',
          mt: showProgressBar ? 6 : 0,
        }}
      >
        <ListPatientFilters
          statusFilter={statusFilter}
          patientsCount={patientsCount}
          updateStatusFilter={updateStatusFilter}
          isFetching={isFetching}
          providerAssociations={providerIdFilterList}
          setProviderIdFilter={handleProviderIdFilterChange}
          providerIdFilter={providerIdFilter ?? ''}
          debouncedSearchQuery={debouncedSearchQuery}
        />
        {searchQuery && (
        <Typography variant='h5' mt={1.5} color={Theme.custom.colors.lightTextPrimary} fontWeight={400}>
          Results matching
          &quot;
          <span className='fs-mask'>{searchQuery}</span>
          &quot;
        </Typography>
        )}
        <Box sx={{
          boxShadow: `
      0px 1px 3px 0px #0000001F,
      0px 1px 1px 0px #00000024,
      0px 2px 1px -1px #00000033
    `,
          borderRadius: 4,
        }}
        >
          <Box
            sx={{
              paddingLeft: 2,
              backgroundColor: isSomethingExpanded
                ? Theme.custom.colors.backgroundColorSecondary
                : Theme.custom.colors.white,
              paddingRight: 2,
              marginTop: '12px',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <ListPatients
              filteredPatientList={filteredPatientList}
              isError={isError}
              isFetching={isFetching}
              setStatusFilter={setStatusFilter}
              showProvider={!!providerAssociations.data?.providers?.length}
              statusFilter={statusFilter as PATIENT_LIST_VIEW}
              sortField={sortField ?? ''}
              sortDirection={sortDirection ?? ''}
              setSortDirection={handleSortDirectionChange}
              setSortField={handleSortFieldChange}
              expandedPatientId={expandedPatientId}
              setExpandedPatientId={handleChangeExpandedPatientId}
              patientCounts={patientsCount}
            />
          </Box>
          <TablePagination
            sx={{
              backgroundColor:
                Theme.custom.colors.white,
              tableLayout: 'fixed',
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
            }}
            component='div'
            count={currentPatientCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      </Box>
    </RefetchPatientsCtx.Provider>
  );
}
